import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Card, Alert, Spinner,
    InfoRow, Input, Select, Toggle,
} from './shared';

const TRANSPORT_OPTIONS = [
    { value: 'http', label: 'HTTP' },
    { value: 'sse', label: 'SSE' },
    { value: 'stdio', label: 'STDIO' },
];

const OWNER_OPTIONS = [
    { value: 'user', label: 'User' },
    { value: 'partner', label: 'Partner' },
    { value: 'system', label: 'System' },
];

const TRANSPORT_BADGE = { http: 'info', sse: 'purple', stdio: 'warning' };

const INITIAL_CREATE = {
    id: '', name: '', description: '',
    transport_type: 'http', server_url: '',
    command: '', args: [], headers: {},
    owner_type: 'user', namespace: '',
};

export default function AdminMCPForm() {
    const { mcpId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.pathname.endsWith('/edit');
    const isCreate = !mcpId;
    const isView = !!mcpId && !isEdit;

    const [data, setData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => { loadData(); }, [mcpId]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (isCreate) {
                setData(INITIAL_CREATE);
                setFormData({ ...INITIAL_CREATE });
            } else {
                const server = await adminApi.getMCPServer(mcpId);
                setData(server);
                setFormData({
                    name: server.name || '',
                    description: server.description || '',
                    transport_type: server.transport_type || 'http',
                    server_url: server.server_url || '',
                    command: server.command || '',
                    args: server.args || [],
                    headers: server.headers || {},
                    owner_type: server.owner_type || 'user',
                    is_active: server.is_active !== false,
                    namespace: server.namespace || '',
                });
            }
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load MCP server');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (isCreate) {
                await adminApi.createMCPServer(formData);
            } else {
                await adminApi.updateMCPServer(mcpId, formData);
            }
            navigate('/admin/mcp');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        try {
            setTestResult({ loading: true });
            const result = await adminApi.testMCPServer(mcpId);
            setTestResult(result);
        } catch (err) {
            setTestResult({ success: false, message: err.message });
        }
    };

    const handleToggleActive = async () => {
        try {
            await adminApi.updateMCPServer(mcpId, { is_active: !data.is_active });
            await loadData();
            setTestResult(null);
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!data && !isCreate) return <Alert variant="error">MCP server not found</Alert>;

    const title = isCreate ? 'Add MCP Server' : isView ? data.name : `Edit: ${mcpId}`;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/mcp" className="text-gray-400 hover:text-gray-600 shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                </Link>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
                        {isView && data.is_builtin && <Badge variant="success">Built-in</Badge>}
                        {isView && (
                            <Badge variant={data.is_active ? 'success' : 'danger'} dot>
                                {data.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                        )}
                    </div>
                    {mcpId && <p className="text-sm text-gray-500 mt-0.5">{mcpId}</p>}
                </div>
                {isView && (
                    <Button size="sm" onClick={() => navigate(`/admin/mcp/${mcpId}/edit`)}>Edit</Button>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            {isView ? (
                <div className="space-y-4">
                    <Card title="Details">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                            <InfoRow label="ID" value={data.id} mono />
                            <InfoRow
                                label="Namespace"
                                value={
                                    <span className="font-mono text-xs">
                                        {data.namespace || data.id?.replace(/-/g, '_')}
                                        <span className="text-gray-400">__*</span>
                                    </span>
                                }
                            />
                            <InfoRow label="Name" value={data.name} />
                            <InfoRow label="Description" value={data.description || '-'} />
                            <InfoRow label="Transport" value={<Badge variant={TRANSPORT_BADGE[data.transport_type] || 'default'}>{data.transport_type?.toUpperCase()}</Badge>} />
                            <InfoRow label="Status" value={data.is_active ? <Badge variant="success" dot>Active</Badge> : <Badge variant="danger" dot>Inactive</Badge>} />
                            {data.server_url && <InfoRow label="URL" value={data.server_url} mono />}
                            {data.command && <InfoRow label="Command" value={data.command} mono />}
                            {data.args?.length > 0 && <InfoRow label="Args" value={data.args.join(' ')} mono />}
                            <InfoRow label="Owner" value={`${data.owner_type}${data.owner_id ? ` (${data.owner_id})` : ''}`} />
                            <InfoRow label="Built-in" value={data.is_builtin ? 'Yes' : 'No'} />
                        </div>
                    </Card>

                    {data.headers && Object.keys(data.headers).length > 0 && (
                        <Card title="Headers">
                            <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono overflow-x-auto">
                                {JSON.stringify(data.headers, null, 2)}
                            </pre>
                        </Card>
                    )}

                    {testResult && (
                        <div className={`p-3 rounded-lg text-sm ${
                            testResult.loading ? 'bg-gray-50 text-gray-600' :
                            testResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                        }`}>
                            {testResult.loading ? (
                                <Spinner size="sm" text="Testing connection..." />
                            ) : (
                                <>
                                    <p className="font-medium text-xs">{testResult.message}</p>
                                    {testResult.success && testResult.tools_count > 0 && (
                                        <div className="mt-2">
                                            <p className="text-xs mb-1">{testResult.tools_count} tools available:</p>
                                            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                                {testResult.tools?.map((tool, idx) => (
                                                    <Badge key={idx} variant="success" className="text-[10px]">{tool}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm" onClick={handleTestConnection}>Test Connection</Button>
                        <Button variant={data.is_active ? 'outline' : 'secondary'} size="sm" onClick={handleToggleActive}>
                            {data.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Card title="General">
                        {isCreate && (
                            <Input
                                label="ID" hint="lowercase, hyphens only"
                                value={formData.id || ''}
                                onChange={(e) => handleChange('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                                placeholder="my-mcp-server" pattern="^[a-z0-9-]+$" required
                            />
                        )}
                        <Input
                            label="Namespace" hint="tool prefix"
                            value={formData.namespace || ''}
                            onChange={(e) => handleChange('namespace', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                            placeholder={isCreate ? (formData.id ? formData.id.replace(/-/g, '_') : 'my_mcp_server') : mcpId?.replace(/-/g, '_')}
                            pattern="^[a-z0-9_]*$"
                        />
                        <Input label="Name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="My MCP Server" required />
                        <Input label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="A brief description" />
                    </Card>

                    <Card title="Connection">
                        <Select label="Transport Type" options={TRANSPORT_OPTIONS} value={formData.transport_type} onChange={(e) => handleChange('transport_type', e.target.value)} />
                        {(formData.transport_type === 'http' || formData.transport_type === 'sse') && (
                            <Input label="Server URL" type="url" value={formData.server_url} onChange={(e) => handleChange('server_url', e.target.value)} placeholder="https://mcp.example.com/api" required={isCreate} />
                        )}
                        {formData.transport_type === 'stdio' && (
                            <Input label="Command" value={formData.command} onChange={(e) => handleChange('command', e.target.value)} placeholder="npx @modelcontextprotocol/server" required={isCreate} />
                        )}
                    </Card>

                    <Card title="Settings">
                        <Select label="Owner Type" options={OWNER_OPTIONS} value={formData.owner_type} onChange={(e) => handleChange('owner_type', e.target.value)} />
                        {!isCreate && (
                            <Toggle label="Active" checked={formData.is_active} onChange={(val) => handleChange('is_active', val)} />
                        )}
                    </Card>

                    <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                        <Button variant="ghost" type="button" onClick={() => navigate('/admin/mcp')}>Cancel</Button>
                        <Button type="submit" loading={saving}>{isCreate ? 'Create Server' : 'Save Changes'}</Button>
                    </div>
                </form>
            )}
        </div>
    );
}
